import React, { useState, useRef } from "react";
import { GrLinkedin } from "react-icons/gr";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "../components/Motion";
import { maskUp, stagger } from "../lib/motion";
import {
  getAllMembers,
  getMembersByCategory,
} from "../data/teamMembers";

const HEADSHOT_BRIGHTEN_IDS = new Set(["akash", "joel"]);

function MemberCard({ id, name, role, image, linkedin }) {
  const brightenHeadshot = id && HEADSHOT_BRIGHTEN_IDS.has(id);
  const objectPositionClass =
    id === "andrew" ? "object-[50%_38%]" : "object-center";
  return (
    <StaggerItem y={18} duration={0.55} className="flex flex-col items-center">
      <div
        className="relative w-32 h-32 sm:w-36 sm:h-36 md:w-40 md:h-40 lg:w-44 lg:h-44 mb-3 rounded-full overflow-hidden bg-gray-200 shadow-md ring-0 ring-icgblue/15 transition-all duration-300 ease-out hover:scale-[1.07] hover:-translate-y-1.5 hover:shadow-xl hover:ring-4 hover:z-10 cursor-default"
      >
        <img
          src={image}
          alt={name}
          loading="eager"
          fetchPriority="low"
          decoding="async"
          className={`h-full w-full origin-center scale-[1.08] object-cover ${objectPositionClass}${brightenHeadshot ? " brightness-110 contrast-[1.02]" : ""}`}
        />
      </div>

      <div className="text-center">
        <h3 className="text-sm md:text-base font-bold text-gray-900">{name}</h3>
        {role && (
          <p className="text-xs text-gray-500">{role}</p>
        )}
        {linkedin && (
          <a
            href={linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center justify-center"
            aria-label={`${name} LinkedIn`}
          >
            <GrLinkedin className="text-xl text-gray-800 hover:text-icgblue transition-colors" />
          </a>
        )}
      </div>
    </StaggerItem>
  );
}

function TabButton({ label, isActive, onClick }) {
  const reduceMotion = useReducedMotion();
  return (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      className={`
        relative px-4 py-2 text-sm md:text-base font-medium
        border transition-colors duration-200
        ${
          isActive
            ? "border-gray-900 text-gray-900 font-bold"
            : "border-gray-300 text-gray-500 hover:border-gray-500 hover:text-gray-700"
        }
      `}
    >
      {isActive && !reduceMotion && (
        <motion.span
          layoutId="team-tab-fill"
          className="absolute inset-0 bg-gray-900/[0.05]"
          transition={{ type: "spring", stiffness: 420, damping: 36, mass: 0.7 }}
        />
      )}
      <span className="relative z-10">{label}</span>
    </button>
  );
}

const TABS = [
  { key: "all", label: "All Members" },
  { key: "executives", label: "Executives" },
  { key: "directors", label: "Directors" },
  { key: "projectManagers", label: "Project Managers" },
  { key: "alumni", label: "Alumni" },
];

/** Sort key for "All Members": President → VPs → Directors → others; stable by source order. */
function memberDisplayTier(member) {
  if (member.role === "President") return 0;
  if (member.role === "Vice President") return 1;
  if (member.categories.includes("directors")) return 2;
  return 3;
}

function sortMembersForAllView(members) {
  const indexById = new Map(members.map((m, i) => [m.id, i]));
  return [...members].sort(
    (a, b) =>
      memberDisplayTier(a) - memberDisplayTier(b) ||
      indexById.get(a.id) - indexById.get(b.id)
  );
}

export default function Team() {
  const [activeTab, setActiveTab] = useState("all");

  const getDisplayMembers = () => {
    switch (activeTab) {
      case "all": {
        const list = getAllMembers().filter(
          (m) =>
            !m.categories.includes("advisors") &&
            !m.categories.includes("alumni")
        );
        return sortMembersForAllView(list);
      }
      case "executives":
        return getMembersByCategory("executives");
      case "directors":
        return getMembersByCategory("directors");
      case "projectManagers":
        return getMembersByCategory("projectManagers");
      case "alumni":
        return getMembersByCategory("alumni");
      default:
        return getAllMembers();
    }
  };

  const advisors = getMembersByCategory("advisors");
  const members = getDisplayMembers();

  const heroRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const plateY = useTransform(scrollYProgress, [0, 1], [0, 44]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, -58]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);

  return (
    <div className="overflow-x-hidden">
      <link
        rel="preload"
        as="image"
        href="/icg-team.webp"
        fetchPriority="high"
      />
      {/* ===== Hero ===== */}
      <div ref={heroRef} className="relative min-h-[80vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        <motion.div
          className="absolute inset-x-0 -top-14 h-[calc(100%+7rem)]"
          style={reduceMotion ? undefined : { y: plateY }}
        >
          <img
            src="/icg-team.webp"
            alt=""
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-center scale-[1.06]"
          />
        </motion.div>
        <div className="absolute inset-0 bg-icgblue/60" />
        <motion.div
          className="relative z-10 text-center px-6"
          style={reduceMotion ? undefined : { y: copyY, opacity: copyOpacity }}
        >
          <motion.div
            variants={reduceMotion ? undefined : stagger(0.13, 0.15)}
            initial={reduceMotion ? undefined : "hidden"}
            animate={reduceMotion ? undefined : "visible"}
          >
            <motion.h1
              variants={reduceMotion ? undefined : maskUp(30, 1)}
              className="text-4xl sm:text-5xl md:text-7xl text-white font-extrabold leading-[1.08] tracking-tighter pb-1"
            >
              A collaborative and intuitive
            </motion.h1>
            <motion.h1
              variants={reduceMotion ? undefined : maskUp(30, 1)}
              className="text-4xl sm:text-5xl md:text-7xl font-extrabold leading-[1.08] tracking-tighter bg-clip-text text-transparent mt-0 pb-1"
              style={{
                backgroundImage: "linear-gradient(to right, #a8d8ff, #ffffff, #a8d8ff)",
              }}
            >
              team you can count on
            </motion.h1>
          </motion.div>
        </motion.div>
      </div>

      {/* ===== Floating Content Panel ===== */}
      <div className="relative z-10 -mt-10 md:-mt-16 bg-white rounded-t-[28px] md:rounded-t-[40px] px-4 md:px-8 pt-8 md:pt-12 pb-16">

        {/* Tab Buttons */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-12">
          {TABS.map((tab) => (
            <TabButton
              key={tab.key}
              label={tab.label}
              isActive={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            />
          ))}
        </div>

        {/* Member Grid — keyed on the tab so switching re-runs the cascade */}
        <div className="container mx-auto max-w-6xl px-2 md:px-6">
          <Stagger
            key={activeTab}
            step={0.035}
            amount={0.02}
            className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-10 md:gap-x-10 md:gap-y-14 place-items-center"
          >
            {members &&
              members.map((member) => (
                <MemberCard
                  key={member.id}
                  id={member.id}
                  name={member.name}
                  role={member.role}
                  image={member.headshotSrc}
                  linkedin={member.linkedinUrl}
                />
              ))}
          </Stagger>
        </div>

        {/* Advisors */}
        <div className="container mx-auto max-w-6xl px-2 md:px-6 pt-20">
          <Reveal
            as="h2"
            preset="mask"
            className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10"
          >
            Advisors
          </Reveal>
          <Stagger
            step={0.08}
            className="flex flex-wrap items-center justify-center gap-10"
          >
            {advisors.map((advisor) => (
              <MemberCard
                key={advisor.id}
                id={advisor.id}
                name={advisor.name}
                role={advisor.role}
                image={advisor.headshotSrc}
                linkedin={advisor.linkedinUrl}
              />
            ))}
          </Stagger>
        </div>

      </div>
    </div>
  );
}
